// Обработчики формы виджета: показатель, территории, годы, визуализация, разрезы, оформление.

import type { VisualizationType } from '../../types';
import { applyAppearanceToSelected, resetAppearanceForSelected, syncKpiIconControls } from '../widget-appearance';
import { applyScopeFilter, dashboardScope } from '../scope-filter';
import { applyYearSelection, bindChoiceMirror, selectLatestYears, selectedValues, syncChoiceMirror } from '../choice-lists';
import { controls, element } from '../controls';
import { currentIndicator, isBdmoIndicator, isSettlementIndicator, loadIndicatorSections, populateIndicators, updateAdvisor } from '../indicators';
import { currentLocale } from '../locale';
import { dimensionControls, expandedAllowedVisualizations, renderVisualizationPalette, selectedDimensionValues, updateClassificationControls, updateConditionControls, updateDimensionSummary, updateIndicatorControls, updateSubmitState, updateVisualizationSpecificControls } from '../widget-form';
import { loadBdmoCatalog, loadSettlementCatalog, rebuildRegionFilter, rebuildTerritoryOptions, updateTerritoryControls, updateTerritoryCount } from '../territories';
import { refreshEmissCatalogStatus, searchLeftEmissCatalog } from '../emiss';
import { refreshWorldBankCatalogStatus, searchWorldBankCatalog } from '../worldbank';
import { state } from '../state';
import { submitWidget } from '../widget-editor';

let indicatorSearchTimer: number | undefined;
let settlementSearchTimer: number | undefined;

export function bindWidgetBuilderEvents(): void {
  bindChoiceMirror(controls.territories, controls.territoryChoiceList);
  bindChoiceMirror(controls.years, controls.yearChoiceList);
  controls.form.addEventListener('submit', (event) => void submitWidget(event as SubmitEvent));
  controls.addAsNewWidget.addEventListener('click', () => void submitWidget(undefined, true));
  controls.source.addEventListener('change', () => {
    controls.indicatorSection.value = '';
    controls.indicatorSearch.value = '';
    void (async () => {
      await loadIndicatorSections();
      populateIndicators();
      if (controls.source.value === 'emiss') void refreshEmissCatalogStatus();
      if (controls.source.value === 'worldbank') void refreshWorldBankCatalogStatus();
      await updateIndicatorControls();
    })();
  });
  controls.indicatorSection.addEventListener('change', () => {
    populateIndicators();
    if (controls.source.value === 'emiss') {
      if (controls.indicatorSearch.value.trim() || state.leftEmissCatalogResults.length) void searchLeftEmissCatalog();
    } else if (controls.source.value === 'worldbank') {
      if (controls.indicatorSearch.value.trim() || state.worldBankCatalogResults.length) void searchWorldBankCatalog();
    } else {
      void updateIndicatorControls();
    }
  });
  controls.indicatorSearch.addEventListener('input', () => {
    window.clearTimeout(indicatorSearchTimer);
    indicatorSearchTimer = window.setTimeout(() => {
      if (controls.source.value === 'emiss') {
        if (controls.indicatorSearch.value.trim() || state.leftEmissCatalogResults.length) void searchLeftEmissCatalog();
      } else if (controls.source.value === 'worldbank') {
        if (controls.indicatorSearch.value.trim() || state.worldBankCatalogResults.length) void searchWorldBankCatalog();
      } else {
        populateIndicators();
        void updateIndicatorControls();
      }
    }, 250);
  });
  controls.indicatorSearch.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (controls.source.value === 'emiss') void searchLeftEmissCatalog();
    else if (controls.source.value === 'worldbank') void searchWorldBankCatalog();
  });
  const appearanceControls: HTMLElement[] = [
    controls.title,
    controls.numberFormat,
    controls.showUnit,
    controls.labelMode,
    controls.tooltip,
    controls.grid,
    controls.animation,
    controls.palette,
    controls.decimals,
    controls.legend,
    controls.labels,
    controls.reverse,
    controls.orientation,
    controls.sortOrder,
    controls.topN,
    controls.axisScale,
    controls.includeZero,
    controls.xAxisMin,
    controls.xAxisMax,
    controls.axisMin,
    controls.axisMax,
    controls.dataZoom,
    controls.showPoints,
    controls.legendPosition,
    controls.smooth,
    controls.patterns,
    controls.lineWidth,
    controls.lineStyle,
    controls.lineCurve,
    controls.connectNulls,
    controls.areaOpacity,
    controls.areaFillMode,
    controls.barLayout,
    controls.barWidth,
    controls.barGap,
    controls.barLabelPosition,
    controls.totalLabels,
    controls.otherThreshold,
    controls.pieVariant,
    controls.pieInnerRadius,
    controls.pieOuterRadius,
    controls.pieLabelMode,
    controls.pieStartAngle,
    controls.pieRoseMode,
    controls.heatmapTranspose,
    controls.heatmapScaleMode,
    controls.heatmapFixedMin,
    controls.heatmapFixedMax,
    controls.heatmapMissingColor,
    controls.heatmapCellGap,
    controls.heatmapCellBorder,
    controls.pyramidSexDimension,
    controls.pyramidAgeDimension,
    controls.pyramidMaleValue,
    controls.pyramidFemaleValue,
    controls.pyramidMode,
    controls.pyramidMaleColor,
    controls.pyramidFemaleColor,
    controls.kpiAggregation,
    controls.kpiComparison,
    controls.kpiTarget,
    controls.kpiSparkline,
    controls.kpiSparklineStyle,
    controls.kpiIconMode,
    controls.kpiIcon,
    controls.tableColumns,
    controls.tableDensity,
    controls.tablePageSize,
    controls.tableCellVisual,
    controls.tableQuality,
    controls.classCount,
    controls.manualBreaks,
    controls.nullColor,
    controls.mapScaleMode,
    controls.mapLegendMode,
    controls.mapBasemap,
    controls.mapFillOpacity,
    controls.mapOutlineColor,
    controls.mapOutlineWidth,
    controls.mapBasemapOpacity,
    controls.mapSymbolMinRadius,
    controls.mapSymbolMaxRadius,
    controls.mapSymbolScale,
    controls.mapSymbolStrokeColor,
    controls.mapSymbolStrokeWidth,
    controls.mapSymbolOpacity,
    controls.mapFixedMin,
    controls.mapFixedMax,
    controls.timeControl,
    controls.conditionValue,
    controls.conditionColor,
  ];
  appearanceControls.forEach((control) => {
    const eventName = control instanceof HTMLInputElement && ['text', 'number', 'color'].includes(control.type)
      ? 'input'
      : 'change';
    control.addEventListener(eventName, () => applyAppearanceToSelected());
  });
  controls.kpiIconMode.addEventListener('change', () => {
    syncKpiIconControls();
    if (state.suppressInspectorEvents) applyAppearanceToSelected(true);
  });
  controls.kpiIcon.addEventListener('input', () => {
    syncKpiIconControls();
    if (state.suppressInspectorEvents) applyAppearanceToSelected(true);
  });
  controls.kpiIconPicker.addEventListener('click', (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>('[data-kpi-icon]');
    if (!button?.dataset.kpiIcon) return;
    controls.kpiIconMode.value = 'custom';
    controls.kpiIcon.value = button.dataset.kpiIcon;
    syncKpiIconControls();
    applyAppearanceToSelected(true);
  });
  controls.indicator.addEventListener('change', () => void updateIndicatorControls());
  controls.territoryLevel.addEventListener('change', () => void updateTerritoryControls(true));
  controls.districtFilter.addEventListener('change', () => {
    rebuildRegionFilter(false);
    if (isBdmoIndicator()) {
      const firstRegion = Array.from(controls.regionFilter.options).find((option) => option.value);
      controls.regionFilter.value = firstRegion?.value ?? '';
      void updateTerritoryControls(true);
    } else if (isSettlementIndicator()) {
      void loadSettlementCatalog([], false);
    } else {
      rebuildTerritoryOptions(undefined, true);
    }
  });
  controls.regionFilter.addEventListener('change', () => {
    if (isBdmoIndicator()) void updateTerritoryControls(true);
    else if (isSettlementIndicator()) void loadSettlementCatalog([], false);
    else rebuildTerritoryOptions(undefined, true);
  });
  controls.territorySearch.addEventListener('input', () => {
    window.clearTimeout(settlementSearchTimer);
    settlementSearchTimer = window.setTimeout(() => {
      if (isSettlementIndicator()) void loadSettlementCatalog([], false);
    }, 250);
  });
  controls.territories.addEventListener('change', () => {
    syncChoiceMirror(controls.territories);
    updateTerritoryCount();
  });
  controls.years.addEventListener('change', () => {
    if (document.activeElement === controls.years || controls.yearChoiceList.contains(document.activeElement)) {
      controls.yearSelectionHint.textContent = '';
    }
    syncChoiceMirror(controls.years);
    updateAdvisor();
    updateSubmitState();
    if (isBdmoIndicator()) void loadBdmoCatalog(selectedValues(controls.territories));
  });
  document.querySelectorAll<HTMLButtonElement>('[data-year-quick]').forEach((button) => {
    button.addEventListener('click', () => selectLatestYears(Number(button.dataset.yearQuick || 0)));
  });
  controls.selectAllYears.addEventListener('click', () => {
    applyYearSelection(Array.from(controls.years.options).filter((option) => !option.disabled).map((option) => option.value));
  });
  controls.clearYears.addEventListener('click', () => {
    applyYearSelection([]);
  });
  controls.months.addEventListener('change', () => {
    updateAdvisor();
    updateSubmitState();
  });
  controls.visualization.addEventListener('change', () => {
    updateVisualizationSpecificControls();
    dimensionControls().forEach(updateDimensionSummary);
    renderVisualizationPalette(expandedAllowedVisualizations(currentIndicator()), controls.visualization.value as VisualizationType);
    updateSubmitState();
  });
  controls.visualizationPalette.addEventListener('click', (event) => {
    const button = (event.target as Element).closest<HTMLButtonElement>('[data-visualization-choice]');
    const choice = button?.dataset.visualizationChoice as VisualizationType | undefined;
    if (!button || !choice || button.disabled) return;
    controls.visualization.value = choice;
    controls.visualization.dispatchEvent(new Event('change'));
    updateAdvisor();
  });
  controls.classification.addEventListener('change', () => {
    updateClassificationControls();
    applyAppearanceToSelected();
  });
  controls.mapScaleMode.addEventListener('change', () => {
    updateClassificationControls();
    applyAppearanceToSelected();
  });
  controls.mapBasemap.addEventListener('change', () => {
    applyAppearanceToSelected();
  });
  controls.conditionOperator.addEventListener('change', () => {
    updateConditionControls();
    applyAppearanceToSelected();
  });
  controls.dimensionFields.addEventListener('change', (event) => {
    const input = (event.target as Element).closest<HTMLInputElement>('input[type="checkbox"]');
    const control = input?.closest<HTMLElement>('[data-dimension-id]');
    if (!control) return;
    const count = control.querySelector<HTMLElement>('[data-dimension-count]');
    if (count) count.textContent = String(selectedDimensionValues(control).length);
    updateDimensionSummary(control);
    updateAdvisor();
  });
  controls.dimensionFields.addEventListener('input', (event) => {
    const search = (event.target as Element).closest<HTMLInputElement>('[data-dimension-search]');
    const control = search?.closest<HTMLElement>('[data-dimension-id]');
    if (!search || !control) return;
    const query = search.value.trim().toLocaleLowerCase(currentLocale() === 'en' ? 'en-US' : 'ru-RU');
    control.querySelectorAll<HTMLElement>('[data-dimension-option]').forEach((option) => {
      option.hidden = Boolean(query) && !(option.dataset.search ?? '').includes(query);
    });
  });
  controls.dimensionFields.addEventListener('click', (event) => {
    const action = (event.target as Element).closest<HTMLButtonElement>('[data-dimension-action]');
    const control = action?.closest<HTMLElement>('[data-dimension-id]');
    if (!action || !control) return;
    const dimension = state.dimensions.find((item) => item.id === control.dataset.dimensionId);
    if (!dimension) return;
    const desired = action.dataset.dimensionAction === 'default'
      ? new Set(dimension.default_values)
      : new Set<string>();
    control.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((input) => {
      input.checked = desired.has(input.value);
    });
    const count = control.querySelector<HTMLElement>('[data-dimension-count]');
    if (count) count.textContent = String(selectedDimensionValues(control).length);
    updateDimensionSummary(control);
    updateAdvisor();
  });
  controls.scopeFilter.addEventListener('change', () => void applyScopeFilter(controls.scopeFilter.value));
  element('#reset-scope-filter').addEventListener('click', () => {
    const scope = dashboardScope();
    if (scope) void applyScopeFilter(scope.territoryId);
  });

  element('#select-all-territories').addEventListener('click', () => {
    Array.from(controls.territories.options).forEach((option) => {
      option.selected = true;
    });
    syncChoiceMirror(controls.territories);
    updateTerritoryCount();
  });
  element('#clear-territories').addEventListener('click', () => {
    Array.from(controls.territories.options).forEach((option) => {
      option.selected = false;
    });
    syncChoiceMirror(controls.territories);
    updateTerritoryCount();
  });
  element('#reset-widget').addEventListener('click', resetAppearanceForSelected);
}
